import React, { useState, useEffect } from "react";
import { Municipality, MayorInfo } from "../types";
import BudgetPieChart from "./BudgetPieChart";
import BudgetComparison from "./BudgetComparison";
import MunicipalitySearch from "./MunicipalitySearch";
import {
  AVAILABLE_YEARS,
  loadBudgetData,
  findBudgetByMatchKey,
} from "../services/budgetDataService";
import {
  fetchMayorInfo,
  formatThaiDate,
  isMayorTermExpiringNov27,
} from "../services/mayorService";
import {
  shareContent,
  generateShareText,
  generateShareUrl,
} from "../utils/shareUtils";
import {
  ISSUE_TAGS,
  IssueAggregation,
  hasVotedRecently,
  submitVote,
  fetchTopIssues,
} from "../services/citizenAgendaService";

// Helper to get icon for issue tag
export const getIssueIcon = (tagId: string) => {
  switch (tagId) {
    case "road":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="18"
          height="18"
        >
          <path d="M2 2h20" />
          <path d="M2 22h20" />
          <path d="M12 2v20" strokeDasharray="4 4" />
        </svg>
      );
    case "water":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="18"
          height="18"
        >
          <path d="M12 2.69l5.74 5.88a6 6 0 0 1-8.48 8.48A6 6 0 0 1 5.53 8.79L11.24 3.08a1 1 0 0 1 1.42-.39" />
          <path d="M12 2.69l-5.74 5.88" />
        </svg>
      );
    case "electricity":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="18"
          height="18"
        >
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      );
    case "garbage":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="18"
          height="18"
        >
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <line x1="10" y1="11" x2="10" y2="17" />
          <line x1="14" y1="11" x2="14" y2="17" />
        </svg>
      );
    case "flood":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="18"
          height="18"
        >
          <path d="M2 22h20" />
          <path d="M2 15h20" />
          <path d="M2 8h20" />
          <path d="M12 2v20" />
        </svg>
      );
    case "education":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="18"
          height="18"
        >
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      );
    case "health":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="18"
          height="18"
        >
          <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
        </svg>
      );
    case "safety":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="18"
          height="18"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      );
    case "park":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="18"
          height="18"
        >
          <path d="M12 22v-9" />
          <path d="M12 13a5 5 0 0 0-5-5c0-2.76 2.24-5 5-5s5 2.24 5 5a5 5 0 0 0-5 5z" />
        </svg>
      );
    case "other":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="18"
          height="18"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      );
    default:
      return null;
  }
};

interface InfoSidebarProps {
  municipality: Municipality | null;
  onMunicipalitySelect?: (municipality: Municipality) => void;
  selectedYear: number;
  onYearChange: (year: number) => void;
}

const InfoSidebar: React.FC<InfoSidebarProps> = ({
  municipality,
  onMunicipalitySelect,
  selectedYear,
  onYearChange,
}) => {
  const [showBudgetBreakdown, setShowBudgetBreakdown] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  const [showThankYou, setShowThankYou] = useState<boolean>(false);
  const [mayorInfo, setMayorInfo] = useState<MayorInfo | null>(null);
  const [loadingMayor, setLoadingMayor] = useState<boolean>(false);
  const [fourYearTotal, setFourYearTotal] = useState<number>(0);
  const [shareStatus, setShareStatus] = useState<string>("");

  // Citizen Agenda voting state
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [customIssue, setCustomIssue] = useState<string>("");
  const [topIssues, setTopIssues] = useState<IssueAggregation[]>([]);
  const [hasVotedToday, setHasVotedToday] = useState<boolean>(false);
  const [successShareStatus, setSuccessShareStatus] = useState<string>("");

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Fetch mayor info when municipality changes
  useEffect(() => {
    if (municipality) {
      setLoadingMayor(true);
      fetchMayorInfo(municipality.name, municipality.province)
        .then((info) => {
          setMayorInfo(info);
          setLoadingMayor(false);
        })
        .catch((error) => {
          console.error("Error loading mayor info:", error);
          setMayorInfo(null);
          setLoadingMayor(false);
        });
    } else {
      setMayorInfo(null);
    }
  }, [municipality]);

  // Load citizen agenda voting data for the municipality
  useEffect(() => {
    if (municipality) {
      const loadVotingData = async () => {
        try {
          // Check if user has already voted today for this municipality
          const hasVoted = hasVotedRecently(municipality.id);
          setHasVotedToday(hasVoted);

          // Load top issues for this municipality
          const topIssuesData = await fetchTopIssues(municipality.id);
          setTopIssues(topIssuesData);

          // Reset selected tags when municipality changes
          setSelectedTags([]);
        } catch (error) {
          console.error("Error loading voting data:", error);
          setTopIssues([]);
        }
      };

      loadVotingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipality]);

  // Calculate 4-year term total budget
  useEffect(() => {
    const calculateFourYearTotal = async () => {
      if (!municipality) {
        setFourYearTotal(0);
        return;
      }

      try {
        // Get the most recent 4 years
        const recentYears = [...AVAILABLE_YEARS]
          .sort((a, b) => b - a)
          .slice(0, 4);
        let total = 0;

        for (const year of recentYears) {
          try {
            const budgetData = await loadBudgetData(year);
            const data = findBudgetByMatchKey(budgetData, municipality.id);
            if (data && data.totalRevenue) {
              // totalRevenue is in million baht, convert to baht
              total += data.totalRevenue * 1000000;
            }
          } catch (error) {
            console.error(`Error loading budget for year ${year}:`, error);
          }
        }

        setFourYearTotal(total);
      } catch (error) {
        console.error("Error calculating 4-year total:", error);
        setFourYearTotal(0);
      }
    };

    calculateFourYearTotal();
  }, [municipality]);

  // Handle tag selection toggle
  const handleTagToggle = (tag: string) => {
    if (hasVotedToday) return;

    setSelectedTags((prev) => {
      if (prev.includes(tag)) {
        return prev.filter((t) => t !== tag);
      } else if (prev.length < 3) {
        // Allow max 3 selections
        return [...prev, tag];
      }
      return prev;
    });
  };

  // Handle citizen issue submission (voting)
  const handleSubmitVote = async () => {
    if (selectedTags.length === 0 || !municipality) return;

    try {
      const result = await submitVote(
        municipality.id,
        municipality.name,
        municipality.province,
        selectedTags as any, // Cast to match expected type
        customIssue // Pass custom comment
      );

      if (result.success) {
        // Mark as voted
        setHasVotedToday(true);

        // Reload top issues
        const topIssuesData = await fetchTopIssues(municipality.id);
        setTopIssues(topIssuesData);

        // Show success state (don't auto-hide, let user see share option)
        setShowThankYou(true);
        setSuccessShareStatus("");
      } else {
        if (result.message?.includes("already voted")) {
          setHasVotedToday(true);
          alert("คุณได้โหวตไปแล้ววันนี้ กรุณากลับมาใหม่พรุ่งนี้");
        } else {
          alert("เกิดข้อผิดพลาดในการส่งความคิดเห็น กรุณาลองใหม่อีกครั้ง");
        }
      }
    } catch (error) {
      console.error("Error submitting vote:", error);
      alert("ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาลองใหม่อีกครั้ง");
    }
  };

  // Handle "Invite Neighbors" share from success state
  const handleInviteNeighbors = async () => {
    if (!municipality) return;

    const shareUrl = generateShareUrl(
      municipality.id,
      municipality.name,
      municipality.budget,
      selectedYear
    );
    const shareTitle = `มาร่วมเสนอวาระประชาชนให้ ${municipality.name}`;
    const shareText = `ฉันเพิ่งโหวตประเด็นที่อยากให้นายกอบต.แก้ไข - มาร่วมแสดงความคิดเห็นกันเถอะ! 🗳️`;

    const result = await shareContent(shareTitle, shareText, shareUrl);

    if (result.success) {
      if (result.method === "clipboard") {
        setSuccessShareStatus("คัดลอกลิงก์แล้ว! ส่งให้เพื่อนบ้านได้เลย");
      } else {
        setSuccessShareStatus("แชร์สำเร็จ!");
      }
      // Clear status after 3 seconds
      setTimeout(() => setSuccessShareStatus(""), 3000);
    } else {
      setSuccessShareStatus("ไม่สามารถแชร์ได้ กรุณาลองใหม่");
      setTimeout(() => setSuccessShareStatus(""), 3000);
    }
  };

  // Dismiss success state
  const handleDismissSuccess = () => {
    setShowThankYou(false);
  };

  // Display different badge classes based on municipality type
  const getBadgeClass = () => {
    if (!municipality) return "";

    const type = municipality.type.toLowerCase();
    if (type.includes("นคร") || type.includes("nakhon")) {
      return "city";
    } else if (type.includes("เมือง") || type.includes("mueang")) {
      return "town";
    } else {
      return "subdistrict";
    }
  };

  // Function to format budget to Thai Baht in millions or billions
  const formatBudget = (budget: number | undefined) => {
    if (!budget) return "ไม่ระบุ";

    if (budget >= 1000000000) {
      // Convert to billions
      return `฿${(budget / 1000000000).toFixed(2)} พันล้านบาท`;
    } else {
      // Convert to millions
      return `฿${(budget / 1000000).toFixed(2)} ล้านบาท`;
    }
  };

  // Function to format population with commas
  const formatPopulation = (population: number | undefined) => {
    if (!population) return "ไม่ระบุ";
    return population.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Format area with comma and units
  const formatArea = (area: number | undefined) => {
    if (!area) return "ไม่ระบุ";
    return `${area.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} ตร.กม.`;
  };

  const handleSelectMunicipality = (selectedMunicipality: Municipality) => {
    if (onMunicipalitySelect) {
      onMunicipalitySelect(selectedMunicipality);
    }
  };

  // Handle share button click
  const handleShare = async () => {
    if (!municipality) return;

    const title = `งบประมาณ ${municipality.name}`;
    const text = generateShareText(
      municipality.name,
      municipality.budget || 0,
      selectedYear
    );
    const url = generateShareUrl(
      municipality.id,
      municipality.name,
      municipality.budget,
      selectedYear
    );

    const result = await shareContent(title, text, url);

    if (result.success) {
      if (result.method === "clipboard") {
        setShareStatus("คัดลอกลิงก์แล้ว!");
        setTimeout(() => setShareStatus(""), 3000);
      }
    } else {
      setShareStatus("ไม่สามารถแชร์ได้");
      setTimeout(() => setShareStatus(""), 3000);
    }
  };

  const renderMunicipalityInfo = () => {
    if (!municipality) {
      return (
        <div className="placeholder-content">
          <img src="/OBT-logo.svg" className="welcome-logo" alt="อบต.ใกล้ฉัน" />
          <h3 className="welcome-title">ยินดีต้อนรับสู่ อบต.ใกล้ฉัน</h3>
          <p>เลือกอบต.บนแผนที่หรือค้นหาอบต.ของคุณ</p>

          {onMunicipalitySelect && (
            <div className="search-section">
              <MunicipalitySearch
                onMunicipalitySelect={handleSelectMunicipality}
              />
            </div>
          )}

          <div className="placeholder-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </div>
        </div>
      );
    }

    return (
      <div className="sidebar-content">
        <div className="sidebar-header">
          <h2>{municipality.name}</h2>
          <button
            className="share-button"
            onClick={handleShare}
            title="แชร์ข้อมูล อบต.นี้"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
          </button>
        </div>
        {shareStatus && <div className="share-status">{shareStatus}</div>}

        <div className="municipality-type">
          <span className={`type-badge ${getBadgeClass()}`}>
            {municipality.type}
          </span>
        </div>

        <div className="info-row">
          <strong>จังหวัด</strong>
          <span>{municipality.province}</span>
        </div>

        <div className="info-row">
          <strong>อำเภอ</strong>
          <span>{municipality.district}</span>
        </div>

        <hr className="section-divider" />

        {/* Mayor Information Section */}
        {mayorInfo && (
          <div className="mayor-info-section">
            <h3 className="mayor-info-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mayor-icon"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              นายกอบต.ปัจจุบัน
            </h3>
            <div className="mayor-details">
              <div className="mayor-name">{mayorInfo.name}</div>
              <div className="mayor-term">
                <div className="mayor-term-item">
                  <span className="term-label">วาระที่:</span>
                  <span className="term-value">{mayorInfo.termYear}</span>
                </div>
                <div className="mayor-term-item">
                  <span className="term-label">เริ่มวาระ:</span>
                  <span className="term-value">
                    {formatThaiDate(mayorInfo.startDate)}
                  </span>
                </div>
                <div className="mayor-term-item">
                  <span className="term-label">สิ้นสุดวาระ:</span>
                  <span className="term-value">
                    {formatThaiDate(mayorInfo.expireDate)}
                  </span>
                </div>
              </div>

              {/* Election Notice */}
              {isMayorTermExpiringNov27(mayorInfo.expireDate) && (
                <div className="election-notice">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="ballot-icon"
                  >
                    <path d="M9 11h6"></path>
                    <path d="M9 15h6"></path>
                    <rect x="4" y="3" width="16" height="18" rx="2"></rect>
                    <path d="M9 7h.01"></path>
                  </svg>
                  <div className="election-text">
                    <strong>มีการเลือกตั้งในเดือนมกราคม 2568</strong>
                    <p>อบต.นี้จะมีการเลือกตั้งนายกอบต.คนใหม่</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="info-row budget">
          <div className="budget-header-row">
            <strong>งบประมาณรายได้รวม</strong>
            <span className="budget-year-display">
              ปี {selectedYear} ({selectedYear - 543})
            </span>
          </div>
          <span className="budget-amount">
            {formatBudget(municipality.budget)}
          </span>

          {/* Year Selector */}
          <div className="budget-year-selector">
            <label htmlFor="sidebar-budget-year">เลือกปีงบประมาณ:</label>
            <select
              id="sidebar-budget-year"
              value={selectedYear}
              onChange={(e) => onYearChange(Number(e.target.value))}
            >
              {AVAILABLE_YEARS.map((year) => (
                <option key={year} value={year}>
                  {year} ({year - 543})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 4-Year Term Total & Comparison */}
        <BudgetComparison
          fourYearTotal={fourYearTotal}
          currentYearBudget={municipality.budget}
        />

        {municipality.budgetSources && (
          <div className="info-row budget-breakdown">
            <button
              className="toggle-button"
              onClick={() => setShowBudgetBreakdown(!showBudgetBreakdown)}
            >
              <strong>
                ที่มาของรายได้{" "}
                <span className="budget-subtitle">รายละเอียดงบประมาณ</span>
                <span className="toggle-icon">
                  {showBudgetBreakdown ? "▼" : "►"}
                </span>
              </strong>
            </button>

            {showBudgetBreakdown && (
              <div className="budget-details">
                <BudgetPieChart
                  budgetSources={municipality.budgetSources}
                  fourYearTotal={fourYearTotal}
                  currentYearBudget={municipality.budget}
                />
                <div className="budget-breakdown-text">
                  <div className="budget-item">
                    <span className="budget-label">จัดเก็บเอง:</span>
                    <span className="budget-value">
                      {municipality.budgetSources.selfCollected?.toFixed(2) ||
                        0}{" "}
                      ล้านบาท
                    </span>
                  </div>
                  <div className="budget-item">
                    <span className="budget-label">รัฐจัดสรร:</span>
                    <span className="budget-value">
                      {municipality.budgetSources.stateAllocated?.toFixed(2) ||
                        0}{" "}
                      ล้านบาท
                    </span>
                  </div>
                  <div className="budget-item">
                    <span className="budget-label">เงินอุดหนุน:</span>
                    <span className="budget-value">
                      {municipality.budgetSources.subsidies?.toFixed(2) || 0}{" "}
                      ล้านบาท
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {loadingMayor && (
          <div className="mayor-loading">
            <div className="loading-spinner"></div>
            <span>กำลังโหลดข้อมูลนายกอบต...</span>
          </div>
        )}

        {!loadingMayor && !mayorInfo && municipality && (
          <div className="mayor-not-available">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="info-icon"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            <span>ไม่พบข้อมูลนายกอบต.ในระบบ</span>
          </div>
        )}

        <div className="stats-container">
          <div className="stat">
            <div className="stat-label">ประชากร</div>
            <div className="stat-value">
              {formatPopulation(municipality.population)}
            </div>
          </div>

          <div className="stat">
            <div className="stat-label">พื้นที่</div>
            <div className="stat-value">{formatArea(municipality.area)}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="info-sidebar">
      {isMobile && (
        <div className="mobile-sidebar-handle">
          <div className="handle-indicator"></div>
        </div>
      )}

      {renderMunicipalityInfo()}

      {/* Sidebar Footer */}
      <div className="sidebar-footer">
        {/* Citizen Agenda Section */}
        {municipality && (
          <div className="citizen-agenda-section">
            <h3 className="citizen-agenda-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="agenda-icon"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              วาระประชาชน
            </h3>
            <p className="citizen-agenda-description">
              เลือกประเด็นที่คุณอยากให้นายกอบต.คนใหม่แก้ไข (เลือกได้สูงสุด 3
              ข้อ)
            </p>

            {!hasVotedToday && !showThankYou ? (
              <div className="voting-container">
                <div className="issue-tags-container">
                  {ISSUE_TAGS.map((tag) => (
                    <button
                      key={tag.id}
                      className={`issue-tag-bubble ${
                        selectedTags.includes(tag.id)
                          ? "issue-tag-selected"
                          : ""
                      }`}
                      onClick={() => handleTagToggle(tag.id)}
                      disabled={hasVotedToday}
                    >
                      {getIssueIcon(tag.id)} {tag.label}
                    </button>
                  ))}
                </div>

                {selectedTags.includes("other") && (
                  <div className="custom-issue-container">
                    <textarea
                      className="custom-issue-input"
                      placeholder="ระบุปัญหาอื่นๆ ที่คุณพบ..."
                      value={customIssue}
                      onChange={(e) => setCustomIssue(e.target.value)}
                      maxLength={200}
                      rows={3}
                    />
                  </div>
                )}

                <button
                  className="submit-vote-btn"
                  onClick={handleSubmitVote}
                  disabled={selectedTags.length === 0}
                >
                  ส่งความคิดเห็น ({selectedTags.length}/3)
                </button>
              </div>
            ) : showThankYou ? (
              <div className="success-state-card">
                <div className="success-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <h4 className="success-title">ขอบคุณค่ะ!</h4>
                <p className="success-message">เสียงของคุณได้ถูกบันทึกแล้ว</p>

                <div className="success-divider"></div>

                <p className="invite-text">ชวนเพื่อนบ้านมาร่วมโหวตกัน!</p>

                <button
                  className="invite-neighbors-btn"
                  onClick={handleInviteNeighbors}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                  {isMobile ? "แชร์ให้เพื่อนบ้าน" : "คัดลอกลิงก์แชร์"}
                </button>

                {successShareStatus && (
                  <div className="success-share-status">
                    {successShareStatus}
                  </div>
                )}

                <button
                  className="dismiss-success-btn"
                  onClick={handleDismissSuccess}
                >
                  ปิด
                </button>
              </div>
            ) : (
              <div className="already-voted-message">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <p>
                  คุณได้ให้ความคิดเห็นไปแล้ววันนี้
                  <br />
                  กลับมาโหวตใหม่ได้พรุ่งนี้นะคะ
                </p>
              </div>
            )}

            {/* Top Issues Display */}
            {topIssues.length > 0 && (
              <div className="top-issues-section">
                <h4>ประเด็นยอดนิยมในพื้นที่:</h4>
                <div className="top-issues-list">
                  {topIssues.slice(0, 5).map((issue, index) => (
                    <div key={issue.tag_id} className="top-issue-item">
                      <span className="top-issue-rank">#{index + 1}</span>
                      <span className="top-issue-tag">
                        {getIssueIcon(issue.tag_id)} {issue.label}
                      </span>
                      <span className="top-issue-count">
                        {issue.vote_count} คน
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InfoSidebar;
